import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';
import { Button } from './button';

import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from '@/components/ui/card';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '@/components/ui/table';

interface FieldConfig {
	index: number;
	label: string;
	type?: string;
	transform?: (value: any) => React.ReactNode;
}

interface GenericTableProps<T> {
	title?: string;
	description?: string;
	data: T[];
	fieldMap?: { [key: string]: FieldConfig };
	maxColumnsMobile: number;
	itemsPerPage: number;
	handleRowClick?: (id: any) => void;
	showUnmapped?: boolean;
	disableVerticalScroll?: boolean; // New prop to disable vertical scroll
}

const GenericTable = <T extends { [key: string]: any }>({
	data,
	title,
	description,
	fieldMap = {},
	maxColumnsMobile,
	itemsPerPage,
	handleRowClick,
	showUnmapped = true,
	disableVerticalScroll = false // Default to false
}: GenericTableProps<T>) => {
	const [currentPage, setCurrentPage] = React.useState(1);

	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentData = data.slice(indexOfFirstItem, indexOfLastItem);

	const allFields = showUnmapped
		? Array.from(new Set([...Object.keys(fieldMap), ...Object.keys(data[0] || {})]))
		: Object.keys(fieldMap);

	const _handleRowClick = (id: any) => {
		if (handleRowClick) {
			handleRowClick(id);
		}
	};

	return (
		<Card className="border-0">
			{(title || description) && (
				<CardHeader>
					<CardTitle>{title}</CardTitle>
					<CardDescription>{description}</CardDescription>
				</CardHeader>
			)}
			<CardContent>
				<div
					className={`${
						disableVerticalScroll
							? 'overflow-x-auto overflow-y-hidden h-full'
							: 'max-h-96 overflow-auto'
					}`}
				>
					<Table>
						<TableHeader>
							<TableRow>
								{allFields.map((field, index) => (
									<TableHead
										key={field}
										className={`${index >= maxColumnsMobile ? 'hidden md:table-cell' : ''}`}
									>
										{fieldMap[field]?.label ||
											field.charAt(0).toUpperCase() + field.slice(1)}
									</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{currentData.map((row, rowIndex) => (
								<TableRow
									key={rowIndex}
									onClick={() => _handleRowClick(row.id)}
									className="cursor-pointer"
								>
									{allFields.map((field, colIndex) => (
										<TableCell
											key={field}
											className={`${
												colIndex >= maxColumnsMobile
													? 'hidden md:table-cell'
													: ''
											} text-ellipsis overflow-hidden whitespace-nowrap`}
										>
											{fieldMap[field]?.transform
												? fieldMap[field].transform!(row[field])
												: row[field] !== undefined
													? !row[field]
														? 'N/A'
														: row[field].toString()
													: 'N/A'}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</CardContent>
			<CardFooter>
				<div className="flex items-center w-full justify-between">
					<div className="text-xs text-muted-foreground">
						Showing{' '}
						<strong>
							{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, data.length)}
						</strong>{' '}
						of <strong>{data.length}</strong> entries
					</div>
					<div className="flex">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
							disabled={currentPage === 1}
						>
							<ChevronLeft className="mr-2 h-4 w-4" />
							Prev
						</Button>
						<Button
							variant="ghost"
							size="sm"
							onClick={() =>
								setCurrentPage((prev) =>
									Math.min(prev + 1, Math.ceil(data.length / itemsPerPage))
								)
							}
							disabled={currentPage === Math.ceil(data.length / itemsPerPage)}
						>
							Next
							<ChevronRight className="ml-2 h-4 w-4" />
						</Button>
					</div>
				</div>
			</CardFooter>
		</Card>
	);
};

export default GenericTable;
